// src/components/DropdownButton.js

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Button from './Button/Button';
import EllipsisVerticalIcon from './Icons/EllipsisVerticalIcon';
import { theme } from '../theme';
import Typography from './Typography';

const DropdownContainer = styled.div`
  display: inline-block;
  position: relative;
`;

const DropdownMenu = styled.ul`
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 4px 0 0;
  min-width: ${({ $minWidth }) => $minWidth || '120px'};
  overflow: hidden;
  padding: ${({ theme }) => theme.sizing.xs};
  padding: 0;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 1100;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.xs};
  
`;

const DropdownMenuItem = styled.li`
  align-items: center;
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.xs};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

// const MenuItemContent = styled.span`
//   color: ${({ $color }) => $color || 'inherit'};
//   flex: 1;
// `;

const MenuItemContent = styled(Typography)`
  color: ${({ $color }) => $color || 'inherit'};
  flex: 1;
`;

const MenuItemIcon = styled.span`
  align-items: center;
  display: flex;
  margin-left: ${({ theme }) => theme.sizing.xs};
`;

const DropdownButton = ({ options, minWidth, buttonVariant = 'outline', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    // Añade un listener para cerrar el menú cuando se hace clic fuera
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Limpia el listener al desmontar el componente
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <DropdownContainer ref={dropdownRef}>
      <Button $variant={buttonVariant} onClick={toggleDropdown} $iconOnly disabled={disabled}>
        <EllipsisVerticalIcon fill={theme.colors.defaultWeak} />
        {/* <img src="/icons/ellipsis-vertical.svg" alt="More options" /> */}
      </Button>
      {isOpen && !disabled && (
        <DropdownMenu $minWidth={minWidth}>
          <InnerWrapper>
            {options.map((option, index) => (
              <DropdownMenuItem
                key={index}
                onClick={() => {
                  option.onClick();
                  setIsOpen(false); // Cierra el menú al seleccionar una opción
                }}
              >
                <MenuItemContent $variant="body-2-medium" $color={option.color}>{option.label}</MenuItemContent>
                {option.icon && <MenuItemIcon>{option.icon}</MenuItemIcon>}
              </DropdownMenuItem>
            ))}
          </InnerWrapper>
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

DropdownButton.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      icon: PropTypes.node,
      color: PropTypes.string,
    })
  ).isRequired,
  minWidth: PropTypes.string,
  buttonVariant: PropTypes.string,
  disabled: PropTypes.bool,
};

export default DropdownButton;
